import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContactsType } from '../../../pages/ifsp/ifspContextApi/IfspContex';
import { ReviewHistoryType } from './ifspSDPAndConsentSlice';

// ------------------ Types ------------------

type Client = {
  name: string | null;
  sex: string | null;
  enrollmentNumber: string | null;
  dateOfBirth: string | null;
};

type ProgramType = {
  name: string | null;
  phoneNumber: string | null;
  imageUrl: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  programImageUrl: string | null;
  dphLogoUrl: string | null;
};

export type MemberType = 'guardian' | 'team' | 'other';

export type GuardianTeamMemberType = {
  id: string | null;
  parentId: string | null;
  uiUuid: string | null;
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  role: string | null;
  memberType: MemberType;
};

export type TeamMemberType = {
  id: string | null;
  teamMemberId: string | null;
  uiUuid: string | null;
  startDate: string | null;
  endDate: string | null;
  discipline: string | null;
  email: string | null;
  name: string | null;
  phoneNumber: string | null;
  role: string | null;
  memberType: MemberType;
};

type OtherTeamMemberType = {
  id: string | null;
  othersId: string | null;
  uiUuid: string | null;
  name: string | null;
  jobDescription: string | null;
  startDate: string | null;
  endDate: string | null;
  memberType: MemberType;
};

export type IfspClientDetailsType = {
  ifspId: string | null;
  primarySettingCode: string | null;
  ifspFirstMeetingDate: string | null;
  ifspSignedDate: string | null;
  justification: string | null;
  reasonForDelay: string | null;
  selectedLanguage: string | null;
  isLocked: boolean;
  version: number;
  client: Client;
  program: ProgramType;
  guardianTeamMember: GuardianTeamMemberType[];
  teamMember: TeamMemberType[];
  otherTeamMember: OtherTeamMemberType[];
  guardians: ContactsType[];
  tbrIfspId: number | null;
  ifspDueDate: string | null;
  initialEvalNotificationDate: string | null;
  reviewHistory: ReviewHistoryType[];
  ifspReviewSignedDate: string | null;
  displayBanner: boolean;
  isConsentSignedAtLeastOnce: boolean;
  tbrUpdateRequired: boolean;
  outcomeTbrUpdateRequired: boolean;
  serviceDeliveryTbrUpdateRequired: boolean;
  transitionTbrUpdateRequired: boolean;
  isCommonAreaChecked: boolean;
  isOutcomeChecked: boolean;
  isServiceDeliveryChecked: boolean;
  isTransitionChecked: boolean;
};

export type InitialIfspClientDetailsType = {
  ifspClientDetails: IfspClientDetailsType;
  originalIfspClientDetails: IfspClientDetailsType;
};

// ------------------ Initial State ------------------

const initialData: IfspClientDetailsType = {
  ifspId: null,
  primarySettingCode: null,
  ifspFirstMeetingDate: null,
  ifspSignedDate: null,
  justification: null,
  reasonForDelay: null,
  selectedLanguage: null,
  isLocked: false,
  version: 0,
  client: {
    name: null,
    sex: null,
    enrollmentNumber: null,
    dateOfBirth: null,
  },
  program: {
    name: null,
    phoneNumber: null,
    fax: null,
    website: null,
    addressLine1: null,
    addressLine2: null,
    city: null,
    state: null,
    zipCode: null,
    programImageUrl: null,
    dphLogoUrl: null,
    imageUrl: null,
    email: null,
  },
  guardianTeamMember: [],
  teamMember: [],
  otherTeamMember: [],
  guardians: [],
  ifspDueDate: null,
  tbrIfspId: null,
  initialEvalNotificationDate: null,
  reviewHistory: [],
  ifspReviewSignedDate: null,
  displayBanner: false,
  isConsentSignedAtLeastOnce: false,
  tbrUpdateRequired: false,
  outcomeTbrUpdateRequired: false,
  serviceDeliveryTbrUpdateRequired: false,
  transitionTbrUpdateRequired: false,
  isCommonAreaChecked: false,
  isOutcomeChecked: false,
  isServiceDeliveryChecked: false,
  isTransitionChecked: false,
};
export const initialIfspClientDetailsState: InitialIfspClientDetailsType = {
  ifspClientDetails: initialData,
  originalIfspClientDetails: initialData,
};

const ifspClientDetailsSlice = createSlice({
  name: 'ifspClientDetails',
  initialState: initialIfspClientDetailsState,
  reducers: {
    setIfspClientDetails: (
      state,
      action: PayloadAction<IfspClientDetailsType>,
    ) => {
      state.ifspClientDetails = structuredClone(action.payload);
      state.originalIfspClientDetails = structuredClone(action.payload);
    },
    updateIfspClientDetails: (
      state,
      action: PayloadAction<Partial<IfspClientDetailsType>>,
    ) => {
      state.ifspClientDetails = {
        ...state.ifspClientDetails,
        ...action.payload,
      };
    },
    updateOriginalIfspClientDetails: (
      state,
      action: PayloadAction<Partial<IfspClientDetailsType>>,
    ) => {
      state.originalIfspClientDetails = {
        ...state.originalIfspClientDetails,
        ...action.payload,
      };
    },
    resetIfspClientDetails: () => initialIfspClientDetailsState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      'RESET_REDUX_APP_STATES',
      () => initialIfspClientDetailsState,
    );
  },
});

export const {
  setIfspClientDetails,
  updateIfspClientDetails,
  updateOriginalIfspClientDetails,
  resetIfspClientDetails,
} = ifspClientDetailsSlice.actions;

export default ifspClientDetailsSlice.reducer;
